import React, { useRef, useEffect, useState, useCallback } from 'react'
import './INEDetector.css'

/**
 * Aspect ratio real de la INE mexicana (85.6 mm × 54 mm)
 * @type {number}
 */
const INE_ASPECT_RATIO = 85.6 / 54 // ≈ 1.585

/**
 * Aspect ratio invertido (INE en posición vertical)
 * @type {number}
 */
const INE_ASPECT_RATIO_VERTICAL = 54 / 85.6 // ≈ 0.631

/**
 * @typedef {Object} INEDetectorProps
 * @property {function} onINEDetected - Callback cuando se detecta y captura la INE. Recibe { image: string, timestamp: string, wasRotated: boolean }
 * @property {function} [onClose] - Callback para cerrar el detector
 * @property {function} [t] - Función de traducción i18n
 * @property {boolean} [embedded] - Si es true, se renderiza dentro de un contenedor (no fixed full-screen)
 * @property {'auto'|'manual'} [captureMode] - Modo de captura: 'auto' captura automáticamente, 'manual' requiere clic del usuario
 */

/**
 * @function INEDetector
 * @description Componente para detectar y capturar una INE usando la cámara del dispositivo.
 * Analiza bordes, contraste, nitidez, aspect ratio y tamaño mínimo del documento dentro del marco.
 * Soporta detección de INE en orientación horizontal y vertical (auto-rota a horizontal).
 * Permite captura automática o manual según la prop captureMode.
 * @param {INEDetectorProps} props
 * @returns {JSX.Element}
 */
const INEDetector = ({ onINEDetected, onClose, t, embedded = false, captureMode = 'auto' }) => {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const overlayCanvasRef = useRef(null)

  // Refs para evitar closures obsoletos en setInterval
  const streamRef = useRef(null)
  const isProcessingRef = useRef(false)
  const consecutiveDetectionsRef = useRef(0)
  const detectionIntervalRef = useRef(null)
  const isVerticalRef = useRef(false)
  const captureModeRef = useRef(captureMode)
  const lastFrameRectRef = useRef(null)
  const captureINERef = useRef(null)

  const [detectionStatus, setDetectionStatus] = useState(
    t ? t('uploadIdentityDocument.modals.camera.searching') : 'Buscando documento...'
  )
  const [confidence, setConfidence] = useState(0)
  const [detectionPhase, setDetectionPhase] = useState('searching')
  const [canManualCapture, setCanManualCapture] = useState(false)
  /** @type {[number|null, function]} Aspect ratio real del stream de video */
  const [videoAspectRatio, setVideoAspectRatio] = useState(null)

  // Mantener ref sincronizado con prop
  useEffect(() => {
    captureModeRef.current = captureMode
  }, [captureMode])

  /**
   * Configuración del área de detección.
   * El marco usa aspect ratio de INE (1.585:1) para guiar al usuario.
   * @type {{ widthPercent: number, minConfidence: number, minAutoConfidence: number, requiredConsecutiveFrames: number, requiredAutoConsecutiveFrames: number, minDocumentFillPercent: number, aspectRatioTolerance: number }}
   */
  const FRAME_CONFIG = {
    widthPercent: 0.85,
    /** Umbral bajo para feedback visual y modo manual */
    minConfidence: 35,
    /** Umbral más alto requerido para auto-captura */
    minAutoConfidence: 50,
    /** Frames consecutivos para feedback visual */
    requiredConsecutiveFrames: 3,
    /** Frames consecutivos para disparar auto-captura */
    requiredAutoConsecutiveFrames: 5,
    minDocumentFillPercent: 0.6,
    aspectRatioTolerance: 0.35,
  }

  /**
   * Calcula el rectángulo del marco de detección con aspect ratio de INE
   * @param {number} w - Ancho del canvas
   * @param {number} h - Alto del canvas
   * @returns {{ frameX: number, frameY: number, frameWidth: number, frameHeight: number }}
   */
  const getFrameRect = (w, h) => {
    const fw = w * FRAME_CONFIG.widthPercent
    const fh = fw / INE_ASPECT_RATIO
    // Si la altura calculada excede el 70% del canvas, ajustar por alto
    const maxH = h * 0.7
    if (fh > maxH) {
      const adjustedH = maxH
      const adjustedW = adjustedH * INE_ASPECT_RATIO
      return {
        frameX: (w - adjustedW) / 2,
        frameY: (h - adjustedH) / 2,
        frameWidth: adjustedW,
        frameHeight: adjustedH,
      }
    }
    return { frameX: (w - fw) / 2, frameY: (h - fh) / 2, frameWidth: fw, frameHeight: fh }
  }

  /** @type {function} Texto traducido o fallback */
  const tt = useCallback((key, fallback) => {
    if (t) return t(key)
    return fallback
  }, [t])

  // ─── Ciclo de vida ───────────────────────────────────────────
  useEffect(() => {
    startCamera()
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current)
        detectionIntervalRef.current = null
      }
      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * @function startCamera
   * @description Inicia la cámara trasera del dispositivo y comienza la detección
   * @returns {Promise<void>}
   */
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })

      streamRef.current = mediaStream

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
        videoRef.current.onloadedmetadata = () => {
          const vw = videoRef.current.videoWidth
          const vh = videoRef.current.videoHeight
          if (vw && vh) {
            setVideoAspectRatio(vw / vh)
          }
          videoRef.current.play()
          drawOverlay('searching')
          startDetection()
        }
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error)
      setDetectionStatus(tt('uploadIdentityDocument.modals.camera.error', 'Error: No se pudo acceder a la cámara'))
    }
  }

  /**
   * @function stopCamera
   * @description Detiene todos los tracks de la cámara
   * @returns {void}
   */
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }

  // ─── Overlay ─────────────────────────────────────────────────

  /**
   * @function drawOverlay
   * @description Dibuja el overlay con el marco de detección con aspect ratio de INE
   * @param {'searching'|'detecting'|'capturing'} phase - Fase actual de detección
   * @returns {void}
   */
  const drawOverlay = (phase = 'searching') => {
    const canvas = overlayCanvasRef.current
    const video = videoRef.current
    if (!canvas || !video) return

    canvas.width = video.videoWidth || 1920
    canvas.height = video.videoHeight || 1080

    const ctx = canvas.getContext('2d')
    const { frameX, frameY, frameWidth, frameHeight } = getFrameRect(canvas.width, canvas.height)

    // Fondo oscuro
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.clearRect(frameX, frameY, frameWidth, frameHeight)

    // Color según fase
    const colors = { searching: '#ffffff', detecting: '#ffcc00', capturing: '#00ff00' }
    ctx.strokeStyle = colors[phase] || '#ffffff'
    ctx.lineWidth = 4
    const cl = 30

    // Borde punteado del frame
    ctx.setLineDash([10, 6])
    ctx.strokeRect(frameX, frameY, frameWidth, frameHeight)
    ctx.setLineDash([])

    // Esquinas
    const corners = [
      [[frameX, frameY + cl], [frameX, frameY], [frameX + cl, frameY]],
      [[frameX + frameWidth - cl, frameY], [frameX + frameWidth, frameY], [frameX + frameWidth, frameY + cl]],
      [[frameX, frameY + frameHeight - cl], [frameX, frameY + frameHeight], [frameX + cl, frameY + frameHeight]],
      [[frameX + frameWidth - cl, frameY + frameHeight], [frameX + frameWidth, frameY + frameHeight], [frameX + frameWidth, frameY + frameHeight - cl]],
    ]
    corners.forEach(([start, mid, end]) => {
      ctx.beginPath()
      ctx.moveTo(...start)
      ctx.lineTo(...mid)
      ctx.lineTo(...end)
      ctx.stroke()
    })

    // Indicador de orientación vertical detectada
    if (isVerticalRef.current && phase === 'detecting') {
      ctx.fillStyle = 'rgba(255, 204, 0, 0.85)'
      ctx.font = `${Math.round(canvas.width * 0.018)}px sans-serif`
      ctx.textAlign = 'center'
      ctx.fillText(
        tt('uploadIdentityDocument.modals.camera.verticalDetected', 'INE en vertical — se rotará automáticamente'),
        canvas.width / 2,
        frameY - 12
      )
    }
  }

  // ─── Detección ───────────────────────────────────────────────

  /**
   * @function startDetection
   * @description Inicia el ciclo de detección cada 400ms
   * @returns {void}
   */
  const startDetection = () => {
    detectionIntervalRef.current = setInterval(() => {
      if (!isProcessingRef.current) {
        detectDocument()
      }
    }, 400)
  }

  /**
   * @function detectDocument
   * @description Analiza el frame actual para detectar si contiene una INE.
   * Evalúa bordes, contraste, nitidez, aspect ratio y tamaño del documento.
   * @returns {void}
   */
  const detectDocument = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || video.readyState < 2) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0)

    const { frameX, frameY, frameWidth, frameHeight } = getFrameRect(canvas.width, canvas.height)
    lastFrameRectRef.current = { frameX, frameY, frameWidth, frameHeight }
    const imageData = ctx.getImageData(frameX, frameY, frameWidth, frameHeight)

    // Análisis de imagen (6 métricas)
    const edgeScore = analyzeEdges(imageData)
    const contrastScore = analyzeContrast(imageData)
    const sharpnessScore = analyzeSharpness(imageData)
    const { score: aspectScore, isVertical } = analyzeAspectRatio(imageData)
    const sizeScore = analyzeDocumentSize(imageData)
    const rectScore = analyzeRectangularity(imageData)

    isVerticalRef.current = isVertical

    // Ponderación: forma (aspectRatio + rectangularity) tiene más peso
    // para filtrar objetos no-rectangulares como caras
    const totalScore =
      edgeScore * 0.10 +
      contrastScore * 0.10 +
      sharpnessScore * 0.10 +
      aspectScore * 0.25 +
      sizeScore * 0.15 +
      rectScore * 0.30

    setConfidence(Math.round(totalScore))

    // En modo auto se requiere un umbral más alto para evitar capturas falsas
    const isAutoMode = captureModeRef.current === 'auto'
    const effectiveConfidence = isAutoMode ? FRAME_CONFIG.minAutoConfidence : FRAME_CONFIG.minConfidence
    const effectiveFrames = isAutoMode ? FRAME_CONFIG.requiredAutoConsecutiveFrames : FRAME_CONFIG.requiredConsecutiveFrames

    if (totalScore >= effectiveConfidence) {
      consecutiveDetectionsRef.current++
      setCanManualCapture(true)

      if (consecutiveDetectionsRef.current >= effectiveFrames) {
        if (isAutoMode) {
          // Captura automática
          setDetectionPhase('capturing')
          setDetectionStatus(tt('uploadIdentityDocument.modals.camera.captured', '¡Documento detectado! Capturando...'))
          drawOverlay('capturing')
          captureINE(frameX, frameY, frameWidth, frameHeight)
        } else {
          // Modo manual: indicar que está listo
          setDetectionPhase('capturing')
          setDetectionStatus(tt('uploadIdentityDocument.modals.camera.captured', '¡Documento detectado! Presiona capturar.'))
          drawOverlay('capturing')
        }
      } else {
        setDetectionPhase('detecting')
        setDetectionStatus(
          `${tt('uploadIdentityDocument.modals.camera.detecting', 'Mantén el documento estable...')} (${consecutiveDetectionsRef.current}/${effectiveFrames})`
        )
        drawOverlay('detecting')
      }
    } else {
      consecutiveDetectionsRef.current = 0
      setCanManualCapture(false)
      setDetectionPhase('searching')

      // Mensajes específicos según lo que falla
      if (aspectScore < 30) {
        setDetectionStatus(
          tt('uploadIdentityDocument.modals.camera.wrongShape', 'No se detecta una INE. Asegúrate de encuadrar la credencial completa')
        )
      } else if (sizeScore < 30) {
        setDetectionStatus(
          tt('uploadIdentityDocument.modals.camera.tooSmall', 'Acerca más el documento al marco')
        )
      } else if (totalScore > 25) {
        setDetectionStatus(
          tt('uploadIdentityDocument.modals.camera.getCloser', 'Acércate más y mantén el documento dentro del marco')
        )
      } else {
        setDetectionStatus(
          tt('uploadIdentityDocument.modals.camera.placeINE', 'Coloca tu INE dentro del marco')
        )
      }
      drawOverlay('searching')
    }
  }

  // ─── Análisis de imagen ──────────────────────────────────────

  /**
   * @function analyzeRectangularity
   * @description Mide si el contenido dentro del frame tiene estructura rectangular.
   * Compara la densidad de bordes en la periferia (donde estarían los bordes de una
   * tarjeta) contra el interior. Un documento rectangular tiene alta concentración de
   * bordes en su perímetro; una cara u objeto orgánico tiene bordes distribuidos
   * uniformemente o concentrados en el centro.
   * @param {ImageData} imageData
   * @returns {number} Score de 0-100
   */
  const analyzeRectangularity = (imageData) => {
    const { data, width, height } = imageData
    const threshold = 30
    const borderW = Math.max(4, Math.round(width * 0.08))
    const borderH = Math.max(4, Math.round(height * 0.08))

    let borderEdges = 0, borderTotal = 0
    let interiorEdges = 0, interiorTotal = 0

    for (let y = 1; y < height - 1; y += 2) {
      for (let x = 1; x < width - 1; x += 2) {
        const idx = (y * width + x) * 4
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3

        const ri = (y * width + x + 1) * 4
        const rGray = (data[ri] + data[ri + 1] + data[ri + 2]) / 3

        const bi = ((y + 1) * width + x) * 4
        const bGray = (data[bi] + data[bi + 1] + data[bi + 2]) / 3

        const isEdge = Math.abs(gray - rGray) > threshold || Math.abs(gray - bGray) > threshold

        const isBorder = x < borderW || x >= width - borderW ||
          y < borderH || y >= height - borderH

        if (isBorder) {
          borderTotal++
          if (isEdge) borderEdges++
        } else {
          interiorTotal++
          if (isEdge) interiorEdges++
        }
      }
    }

    if (borderTotal === 0 || interiorTotal === 0) return 0

    const borderDensity = borderEdges / borderTotal
    const interiorDensity = interiorEdges / interiorTotal

    // ratio > 1 → más bordes en el perímetro (forma de tarjeta)
    // ratio ≈ 1 → bordes distribuidos uniformemente (cara, objeto orgánico)
    // ratio < 1 → más bordes en el centro (cara)
    if (interiorDensity === 0) return borderDensity > 0.05 ? 70 : 0

    const ratio = borderDensity / interiorDensity

    if (ratio < 0.8) return 0
    if (ratio >= 2.5) return 100

    return Math.min(100, ((ratio - 0.8) / 1.7) * 100)
  }

  /**
   * @function analyzeEdges
   * @description Detecta bordes usando diferencias de gradiente, muestreando cada 2 píxeles
   * @param {ImageData} imageData
   * @returns {number} Score de 0-100
   */
  const analyzeEdges = (imageData) => {
    const { data, width, height } = imageData
    let edgeCount = 0
    const threshold = 30

    for (let y = 1; y < height - 1; y += 2) {
      for (let x = 1; x < width - 1; x += 2) {
        const idx = (y * width + x) * 4
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3

        const ri = (y * width + x + 1) * 4
        const rGray = (data[ri] + data[ri + 1] + data[ri + 2]) / 3

        const bi = ((y + 1) * width + x) * 4
        const bGray = (data[bi] + data[bi + 1] + data[bi + 2]) / 3

        if (Math.abs(gray - rGray) > threshold || Math.abs(gray - bGray) > threshold) {
          edgeCount++
        }
      }
    }

    const sampledPixels = Math.floor((width - 2) / 2) * Math.floor((height - 2) / 2)
    return Math.min(100, (edgeCount / sampledPixels) * 500)
  }

  /**
   * @function analyzeContrast
   * @description Analiza la desviación estándar de luminosidad para medir contraste
   * @param {ImageData} imageData
   * @returns {number} Score de 0-100
   */
  const analyzeContrast = (imageData) => {
    const { data } = imageData
    let sum = 0
    let sumSq = 0
    let sampled = 0

    for (let i = 0; i < data.length; i += 16) {
      const gray = (data[i] + data[i + 1] + data[i + 2]) / 3
      sum += gray
      sumSq += gray * gray
      sampled++
    }

    const mean = sum / sampled
    const variance = sumSq / sampled - mean * mean
    const stdDev = Math.sqrt(Math.max(0, variance))

    return Math.min(100, (stdDev / 60) * 100)
  }

  /**
   * @function analyzeSharpness
   * @description Mide la nitidez de la imagen con un filtro Laplaciano
   * @param {ImageData} imageData
   * @returns {number} Score de 0-100
   */
  const analyzeSharpness = (imageData) => {
    const { data, width, height } = imageData
    let laplacianSum = 0
    let count = 0

    for (let y = 1; y < height - 1; y += 3) {
      for (let x = 1; x < width - 1; x += 3) {
        const idx = (y * width + x) * 4
        const c = (data[idx] + data[idx + 1] + data[idx + 2]) / 3

        const tIdx = ((y - 1) * width + x) * 4
        const tVal = (data[tIdx] + data[tIdx + 1] + data[tIdx + 2]) / 3
        const lIdx = (y * width + x - 1) * 4
        const lVal = (data[lIdx] + data[lIdx + 1] + data[lIdx + 2]) / 3
        const rIdx = (y * width + x + 1) * 4
        const rVal = (data[rIdx] + data[rIdx + 1] + data[rIdx + 2]) / 3
        const bIdx = ((y + 1) * width + x) * 4
        const bVal = (data[bIdx] + data[bIdx + 1] + data[bIdx + 2]) / 3

        laplacianSum += Math.abs(4 * c - tVal - lVal - rVal - bVal)
        count++
      }
    }

    const avg = laplacianSum / count
    return Math.min(100, (avg / 15) * 100)
  }

  /**
   * @function analyzeAspectRatio
   * @description Detecta el bounding box del documento dentro del frame y evalúa
   * si su aspect ratio coincide con una INE (horizontal ≈1.585 o vertical ≈0.631).
   * Usa proyecciones de densidad de bordes (horizontal y vertical) para encontrar los límites.
   * @param {ImageData} imageData
   * @returns {{ score: number, isVertical: boolean }}
   */
  const analyzeAspectRatio = (imageData) => {
    const { data, width, height } = imageData
    const threshold = 35

    // Proyección horizontal: densidad de bordes por columna
    /** @type {number[]} */
    const colDensity = new Array(width).fill(0)
    // Proyección vertical: densidad de bordes por fila
    /** @type {number[]} */
    const rowDensity = new Array(height).fill(0)

    // Muestrear cada 3 píxeles para rendimiento
    for (let y = 1; y < height - 1; y += 3) {
      for (let x = 1; x < width - 1; x += 3) {
        const idx = (y * width + x) * 4
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3

        const ri = (y * width + x + 1) * 4
        const rGray = (data[ri] + data[ri + 1] + data[ri + 2]) / 3

        const bi = ((y + 1) * width + x) * 4
        const bGray = (data[bi] + data[bi + 1] + data[bi + 2]) / 3

        if (Math.abs(gray - rGray) > threshold || Math.abs(gray - bGray) > threshold) {
          colDensity[x]++
          rowDensity[y]++
        }
      }
    }

    // Encontrar bordes del documento: columnas/filas donde la densidad supera un umbral
    const colMax = Math.max(...colDensity)
    const rowMax = Math.max(...rowDensity)
    const colThreshold = colMax * 0.25
    const rowThreshold = rowMax * 0.25

    let leftEdge = 0
    let rightEdge = width - 1
    let topEdge = 0
    let bottomEdge = height - 1

    for (let x = 0; x < width; x++) {
      if (colDensity[x] >= colThreshold) { leftEdge = x; break }
    }
    for (let x = width - 1; x >= 0; x--) {
      if (colDensity[x] >= colThreshold) { rightEdge = x; break }
    }
    for (let y = 0; y < height; y++) {
      if (rowDensity[y] >= rowThreshold) { topEdge = y; break }
    }
    for (let y = height - 1; y >= 0; y--) {
      if (rowDensity[y] >= rowThreshold) { bottomEdge = y; break }
    }

    const docWidth = rightEdge - leftEdge
    const docHeight = bottomEdge - topEdge

    // Si el bounding box es demasiado pequeño, no hay documento
    if (docWidth < width * 0.2 || docHeight < height * 0.2) {
      return { score: 0, isVertical: false }
    }

    const detectedRatio = docWidth / docHeight

    // Comparar con aspect ratio de INE horizontal
    const diffHorizontal = Math.abs(detectedRatio - INE_ASPECT_RATIO) / INE_ASPECT_RATIO
    // Comparar con aspect ratio de INE vertical
    const diffVertical = Math.abs(detectedRatio - INE_ASPECT_RATIO_VERTICAL) / INE_ASPECT_RATIO_VERTICAL

    const tolerance = FRAME_CONFIG.aspectRatioTolerance
    let score = 0
    let isVertical = false

    if (diffHorizontal <= tolerance) {
      // INE horizontal: a menor diferencia, mayor score
      score = Math.max(0, 100 - (diffHorizontal / tolerance) * 100)
    } else if (diffVertical <= tolerance) {
      // INE vertical detectada
      score = Math.max(0, 100 - (diffVertical / tolerance) * 100)
      isVertical = true
    } else {
      // No coincide con ninguna orientación de INE
      score = Math.max(0, 20 - Math.min(diffHorizontal, diffVertical) * 50)
    }

    return { score: Math.min(100, score), isVertical }
  }

  /**
   * @function analyzeDocumentSize
   * @description Evalúa qué porcentaje del frame de detección ocupa el documento.
   * Rechaza documentos que ocupen menos del minDocumentFillPercent.
   * @param {ImageData} imageData
   * @returns {number} Score de 0-100
   */
  const analyzeDocumentSize = (imageData) => {
    const { data, width, height } = imageData
    const threshold = 30

    // Contar píxeles con borde significativo como proxy del área del documento
    let edgePixels = 0
    let total = 0

    for (let y = 1; y < height - 1; y += 4) {
      for (let x = 1; x < width - 1; x += 4) {
        const idx = (y * width + x) * 4
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3

        const ri = (y * width + x + 1) * 4
        const rGray = (data[ri] + data[ri + 1] + data[ri + 2]) / 3

        const bi = ((y + 1) * width + x) * 4
        const bGray = (data[bi] + data[bi + 1] + data[bi + 2]) / 3

        if (Math.abs(gray - rGray) > threshold || Math.abs(gray - bGray) > threshold) {
          edgePixels++
        }
        total++
      }
    }

    const fillRatio = edgePixels / total

    // Un documento real ocupa al menos edge-pixels en el frame
    // Escalar: <2% → 0, 2%-10% → 0-100
    const minFill = 0.02
    const maxFill = 0.10
    if (fillRatio < minFill) return 0
    if (fillRatio >= maxFill) return 100

    return ((fillRatio - minFill) / (maxFill - minFill)) * 100
  }

  // ─── Captura ─────────────────────────────────────────────────

  /**
   * @function captureINE
   * @description Captura la imagen de la INE del frame actual. Si la INE está
   * en orientación vertical, la rota 90° a horizontal antes de entregar.
   * @param {number} x - Coordenada X del recorte
   * @param {number} y - Coordenada Y del recorte
   * @param {number} w - Ancho del recorte
   * @param {number} h - Alto del recorte
   * @returns {void}
   */
  const captureINE = (x, y, w, h) => {
    if (isProcessingRef.current) return
    isProcessingRef.current = true

    // Detener detección
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current)
      detectionIntervalRef.current = null
    }

    const canvas = canvasRef.current
    const wasRotated = isVerticalRef.current

    // Canvas temporal con el área recortada
    const tmp = document.createElement('canvas')

    if (wasRotated) {
      // Rotar 90° para entregar la INE en horizontal
      tmp.width = h
      tmp.height = w
      const tmpCtx = tmp.getContext('2d')
      tmpCtx.translate(h / 2, w / 2)
      tmpCtx.rotate(-Math.PI / 2)
      tmpCtx.drawImage(canvas, x, y, w, h, -w / 2, -h / 2, w, h)
    } else {
      tmp.width = w
      tmp.height = h
      tmp.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, w, h)
    }

    const imageDataUrl = tmp.toDataURL('image/jpeg', 0.92)
    setDetectionStatus(tt('uploadIdentityDocument.modals.camera.capturedSuccess', '¡INE capturada exitosamente!'))

    setTimeout(() => {
      stopCamera()
      onINEDetected({
        image: imageDataUrl,
        timestamp: new Date().toISOString(),
        wasRotated,
      })
    }, 800)
  }

  // Mantener ref sincronizado con la última versión de captureINE
  captureINERef.current = captureINE

  /**
   * @function handleManualCapture
   * @description Handler para el botón de captura manual.
   * Usa captureINERef para evitar stale closures.
   * Si lastFrameRectRef no tiene valor, calcula el rectángulo on-demand.
   * @returns {void}
   */
  const handleManualCapture = useCallback(() => {
    // Asegurar que el canvas tenga el frame actual dibujado
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || video.readyState < 2) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d').drawImage(video, 0, 0)

    // Usar lastFrameRect si existe, o calcular on-demand
    const rect = lastFrameRectRef.current || getFrameRect(canvas.width, canvas.height)
    const { frameX, frameY, frameWidth, frameHeight } = rect
    captureINERef.current(frameX, frameY, frameWidth, frameHeight)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Render ──────────────────────────────────────────────────

  /**
   * @type {Object|undefined} Estilo inline dinámico para el contenedor embebido.
   * Ajusta el aspect-ratio al del stream de video real para evitar barras negras
   * y asegurar alineación perfecta entre video y overlay en cualquier dispositivo.
   */
  const embeddedStyle = embedded && videoAspectRatio
    ? { aspectRatio: `${videoAspectRatio}` }
    : undefined

  return (
    <div
      className={embedded ? 'ine-detector ine-detector--embedded' : 'ine-detector'}
      style={embeddedStyle}
    >
      <div className="camera-container">
        <video ref={videoRef} autoPlay playsInline muted className="camera-feed" />
        <canvas ref={overlayCanvasRef} className="overlay-canvas" />
        <canvas ref={canvasRef} className="hidden-canvas" />

        {captureMode === 'manual' && (
          <button
            type="button"
            className="ine-capture-button ine-capture-button--active"
            onClick={handleManualCapture}
            aria-label={tt('uploadIdentityDocument.modals.camera.captureButton', 'Capturar')}
          >
            <span className="ine-capture-button__inner" />
          </button>
        )}
      </div>

      <div className="detection-info">
        <div className="status-bar">
          {/* <span className={`status-text status-${detectionPhase}`}>{detectionStatus}</span> */}
          {confidence > 0 && <span className="confidence-text">{confidence}%</span>}
        </div>
      </div>
    </div>
  )
}

export default INEDetector
